'use client';

/**
 * 崩溃监控 + 性能监控（v1.0 正式发布包）：
 *  - window error / unhandledrejection → 上报 app_error（含堆栈与路由）
 *  - 首屏性能（navigation timing + LCP）→ 上报 app_perf
 *  - 慢接口（>1500ms）→ 上报 app_perf（slow_api）
 * 数据落到 analytics_events 表，后台「监控审计 / 性能」可查。
 * 轻量、fire-and-forget，任何上报失败静默忽略（不递归、不影响业务）。
 */
import { api } from './api';
import { getDevice, getSessionId } from './analytics';

let booted = false;

const SLOW_API_MS = 1500;
const MAX_STACK = 2000;
const MAX_MSG = 160;

function report(event: string, target: string, meta?: Record<string, unknown>) {
  try {
    void api
      .post('/track/event', {
        event,
        target: String(target).slice(0, 128),
        sessionId: getSessionId(),
        device: getDevice(),
        meta,
      })
      .catch(() => {
        /* fire-and-forget */
      });
  } catch {
    /* never break the app */
  }
}

/** 保留每位用户/会话对同一慢接口只记一次的缓存，避免刷屏 */
const slowApiSeen = new Set<string>();
function reportSlowApi(url: string, ms: number) {
  const key = `${url}|${Math.round(ms / 2000)}`; // 2s 粒度
  if (slowApiSeen.has(key)) return;
  slowApiSeen.add(key);
  if (slowApiSeen.size > 200) slowApiSeen.clear();
  report('app_perf', 'slow_api', { url: url.slice(0, 160), ms: Math.round(ms), route: location.pathname });
}

export function initMonitor() {
  if (booted || typeof window === 'undefined') return;
  booted = true;

  // ---- 崩溃：JS 运行时错误 ----
  window.addEventListener(
    'error',
    (e) => {
      const msg = (e.message || 'unknown error').slice(0, MAX_MSG);
      const stack = e.error?.stack ? String(e.error.stack).slice(0, MAX_STACK) : undefined;
      report('app_error', msg, { stack, route: location.pathname, ua: navigator.userAgent.slice(0, 160) });
    },
    true,
  );

  // ---- 崩溃：未处理的 Promise rejection ----
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const msg =
      (typeof reason === 'string'
        ? reason
        : reason?.message
          ? String(reason.message)
          : 'unhandled rejection').slice(0, MAX_MSG);
    const stack = reason?.stack ? String(reason.stack).slice(0, MAX_STACK) : undefined;
    report('app_error', msg, { rejection: true, stack, route: location.pathname });
  });

  // ---- 性能：首屏关键指标（navigation timing，load 后一次） ----
  const perfSent = { nav: false, lcp: false };
  const sendNavTiming = () => {
    if (perfSent.nav) return;
    perfSent.nav = true;
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (!nav) return;
      report('app_perf', 'page_load', {
        dns: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
        tcp: Math.round(nav.connectEnd - nav.connectStart),
        ttfb: Math.round(nav.responseStart - nav.requestStart),
        domContentLoaded: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
        load: Math.round(nav.loadEventEnd - nav.startTime),
        route: location.pathname,
      });
    } catch {
      /* ignore */
    }
  };
  if (document.readyState === 'complete') sendNavTiming();
  else window.addEventListener('load', sendNavTiming, { once: true });

  // ---- 性能：LCP（最大内容绘制，反映首屏视觉完成） ----
  try {
    const observer = new PerformanceObserver((list) => {
      if (perfSent.lcp) return;
      const entries = list.getEntries();
      const lcp = entries[entries.length - 1] as PerformanceEntry & { startTime?: number };
      if (!lcp?.startTime) return;
      perfSent.lcp = true;
      report('app_perf', 'lcp', { ms: Math.round(lcp.startTime), route: location.pathname });
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    /* PerformanceObserver 不支持则跳过 */
  }

  // ---- 性能：慢接口（>1.5s），只统计业务/公开接口，不统计埋点自身 ----
  const origFetch = window.fetch;
  window.fetch = function wrappedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const start = performance.now();
    return origFetch.apply(this, [input, init]).then(
      (res) => {
        try {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
          const ms = performance.now() - start;
          if (ms > SLOW_API_MS && !url.includes('/track/')) reportSlowApi(url, ms);
        } catch {
          /* ignore */
        }
        return res;
      },
      (err: unknown) => {
        try {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
          const ms = performance.now() - start;
          if (ms > SLOW_API_MS && !url.includes('/track/')) reportSlowApi(url, ms);
        } catch {
          /* ignore */
        }
        throw err;
      },
    );
  };
}

/** 监听断网恢复事件，重连后刷新当前页数据（供数据组件主动重拉）。 */
export function onOnline(cb: () => void): () => void {
  const h = () => cb();
  window.addEventListener('ailumix:online', h);
  return () => window.removeEventListener('ailumix:online', h);
}
